import React from "react";
import { Layers, Instagram, Facebook, Linkedin, Globe, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageBanner from "./PageBanner";

const subunitsData = [
  {
    id: 1,
    name: "CS",
    fullName: "Computer Society",
    logo: "/asset/logos/cswh.png",
    bannerImage: "/asset/banners/csg.webp",
    description:
      "The IEEE Computer Society (CS) students branch chapter at isims is focused on advancing the fields of computing and technology. It provides students with opportunities to participate in workshops, coding competitions, and technical talks.",
    social: { instagram: "https://www.instagram.com/ieee.isims.cs/", facebook: "https://www.facebook.com/profile.php?id=100088090993586", linkedin: "https://www.linkedin.com/company/ieee-computer-society-chapter-isims-student-branch/posts/?feedView=all", website: "https://cs-isims.ieee.tn/" }
  },
  {
    id: 2,
    name: "IAS",
    fullName: "Industrial Applications Society",
    logo: "/asset/logos/iasjdid.png",
    bannerImage: "/asset/banners/iasg.webp",
    description:
      "The IEEE IAS students branch chapter at isims bridges the gap between students and the industry, focusing on applying engineering and scientific principles to solve industrial problems.",
    social: { instagram: "https://www.instagram.com/ieee_ias_isims/", facebook: "https://www.facebook.com/profile.php?id=61572324778458", linkedin: "https://www.linkedin.com/company/ieee-ias-isims-sbc/posts/?feedView=all", website: "https://ias-isims.ieee.tn/" }
  },
  {
    id: 3,
    name: "IES-PES",
    fullName: "Power & Energy Society",
    logo: "/asset/logos/iespeswh.png",
    bannerImage: "/asset/banners/iesg.webp",
    description:
      "The IEEE IES and PES students branch chapter at isims focuses on the application of electronics in industrial processes and the generation, transmission, and distribution of electrical energy.",
    social: { instagram: "https://www.instagram.com/ieee.ies.pes.isims.sb.jc/", facebook: "https://www.facebook.com/profile.php?id=61580047484275", linkedin: "https://www.linkedin.com/company/ieee-ies-pes-student-branch-chapter-%E2%80%93-isims/posts/?feedView=all", website: "https://ies-pes-isims.ieee.tn/" }
  },
  {
    id: 4,
    name: "WIE",
    fullName: "Women in Engineering",
    logo: "/asset/logos/wiejdid.png",
    bannerImage: "/asset/banners/wieg.webp",
    description:
      "The Women in Engineering (WIE) affinity group at isims focuses on promoting gender diversity in engineering and technology. It provides a platform for female engineers to connect, collaborate, and contribute.",
    social: { instagram: "https://www.instagram.com/ieee_wie_isims_sag/", facebook: "https://www.facebook.com/profile.php?id=100088426925351", linkedin: "https://www.linkedin.com/in/ieee-wie-isims-student-affinity-group-2a7a4035a/", website: "https://wie-isims.ieee.tn/" }
  },
  {
    id: 5,
    name: "SIGHT",
    fullName: "Humanitarian Technology",
    logo: "/asset/logos/sightwh.png",
    bannerImage: "/asset/banners/sightg.webp",
    description:
      "The IEEE SIGHT isims Student Branch Group aims to promote IEEE Humanitarian Technologies Activities and SIGHT goals within IEEE isims SB Community. Activities are mainly focused on sustainable SDG oriented technical projects.",
    social: { instagram: "https://www.instagram.com/ieee.isims.sight.group/", facebook: "https://www.facebook.com/profile.php?id=61584359810922", linkedin: "#", website: "#" }
  },
  {
    id: 6,
    name: "SMC",
    fullName: "Systems, Man & Cybernetics",
    logo: "/asset/logos/smcwh.png",
    bannerImage: "/asset/banners/smcg.webp",
    description:
      "The IEEE SMC students branch chapter at isims focuses on the interdisciplinary study of systems engineering, human-machine interaction, and intelligent systems.",
    social: { instagram: "https://www.instagram.com/ieee_smc_isims_sb_chapter/", facebook: "https://www.facebook.com/profile.php?id=61557150934003", linkedin: "https://www.linkedin.com/search/results/all/?keywords=smc%20isims&origin=GLOBAL_SEARCH_HEADER", website: "https://smc-isims.ieee.tn/" }
  },
  {
    id: 7,
    name: "RAS",
    fullName: "Robotics & Automation",
    logo: "/asset/logos/raswh.png",
    bannerImage: "/asset/banners/rasg.webp",
    description:
      "The Robotics and Automation Society (RAS) students branch chapter at isims is a hub for students interested in the fields of robotics, automation, and artificial intelligence.",
    social: { instagram: "https://www.instagram.com/ieee_ras_chapter_isims_sb/", facebook: "https://www.facebook.com/profile.php?id=100090530634047", linkedin: "linkedin.com/search/results/all/?keywords=ieee%20ras%20isims&origin=GLOBAL_SEARCH_HEADER", website: "https://ras-isims.ieee.tn/" }
  },
];

export default function SubunitsPage() {
  return (
    <div className="min-h-screen bg-[#fafcff]">
      <PageBanner
        title="Our Subunits"
        description="Explore the technical chapters and affinity groups within IEEE ISIMS Student Branch, each driving innovation in their specialized fields."
        Icon={Layers}
      />
      
      <div id="subunits-list" className="relative bg-[#fafcff] w-full py-20 overflow-hidden">
        
        {/* Texture Background as requested */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              repeating-linear-gradient(
                -45deg,
                rgba(15, 105, 157, 0.05) 0px,
                rgba(15, 105, 157, 0.05) 1px,
                transparent 1px,
                transparent 18px
              ),
              repeating-linear-gradient(
                45deg,
                rgba(15, 105, 157, 0.04) 0px,
                rgba(15, 105, 157, 0.04) 1px,
                transparent 1px,
                transparent 28px
              )
            `,
          }}
        />

        <div className="relative w-[90%] max-w-[1400px] mx-auto">
          
          <div className="space-y-16">
            {subunitsData.map((unit, index) => {
              const isEven = index % 2 === 0;

              return (
                <motion.div
                  key={unit.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5 }}
                  className="relative bg-white border border-[#006699]/20 shadow-xl overflow-hidden rounded-none group"
                >
                  {/* L Corners for the box */}
                  <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-[#006699]/50 z-20 pointer-events-none" />
                  <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-[#006699]/50 z-20 pointer-events-none" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-[#006699]/50 z-20 pointer-events-none" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-[#006699]/50 z-20 pointer-events-none" />

                  <div className="flex flex-col md:flex-row h-full">
                    
                    {/* Banner Side */}
                    <div className={`relative w-full md:w-1/2 h-[180px] md:h-auto overflow-hidden bg-black ${isEven ? 'md:order-1' : 'md:order-2'}`}>
                      <img
                        src={unit.bannerImage}
                        alt={`${unit.name} banner`}
                        className="absolute inset-0 w-full h-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
                      />
                      
                      {/* Centered Big Logo */}
                      <div className="absolute inset-0 flex items-center justify-center z-10">
                        <img
                          src={unit.logo}
                          alt={`${unit.name} logo`}
                          className="w-24 h-24 md:w-32 md:h-32 object-contain brightness-0 invert drop-shadow-2xl"
                        />
                      </div>
                    </div>

                    {/* Content Side */}
                    <div className={`w-full md:w-1/2 p-8 md:p-10 flex flex-col justify-center ${isEven ? 'md:order-2' : 'md:order-1'}`}>
                      
                      <h3 className="text-3xl font-black text-[#00334d] uppercase tracking-tighter mb-1">
                        {unit.name}
                      </h3>
                      <h4 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-6">
                        {unit.fullName}
                      </h4>
                      
                      <p className="text-gray-600 text-sm leading-relaxed mb-8">
                        {unit.description}
                      </p>
                      
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                        {/* Social Icons */}
                        <div className="flex items-center gap-3">
                          {unit.social.instagram && unit.social.instagram !== "#" && (
                            <SocialIcon href={unit.social.instagram} icon={<Instagram />} />
                          )}
                          {unit.social.facebook && unit.social.facebook !== "#" && (
                            <SocialIcon href={unit.social.facebook} icon={<Facebook />} />
                          )}
                          {unit.social.linkedin && unit.social.linkedin !== "#" && (
                            <SocialIcon href={unit.social.linkedin} icon={<Linkedin />} />
                          )}
                          {unit.social.website && unit.social.website !== "#" && (
                            <SocialIcon href={unit.social.website} icon={<Globe />} />
                          )}
                        </div>

                        {/* Register Button with L corners */}
                        <Link 
                          to={`/${unit.name.toLowerCase()}register`} 
                          className="relative group/btn bg-[#f4f6f9] border border-transparent hover:border-[#006699]/40 text-[#00334d] px-8 py-3.5 text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all duration-300"
                        >
                          {/* Button L Corners */}
                          <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-[#006699] opacity-0 group-hover/btn:opacity-100 transition-opacity pointer-events-none" />
                          <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-[#006699] opacity-0 group-hover/btn:opacity-100 transition-opacity pointer-events-none" />
                          
                          Register Now
                          <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
}

function SocialIcon({ href, icon }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="p-2.5 bg-[#f4f6f9] text-[#00334d] rounded-none hover:bg-[#006699] hover:text-white transition-colors duration-300 shadow-sm border border-transparent hover:border-[#006699]"
    >
      {React.cloneElement(icon, { className: "w-4 h-4" })}
    </a>
  );
}
